import { type FormEvent, useRef, useState } from 'react'
import {
  changeMasterPasswordFlow,
  MasterPasswordError,
  type ChangeMasterPasswordInput,
  type MasterPasswordFlowDeps,
} from '../services/masterPasswordFlow'

/**
 * `pages/settings/ChangeMasterPasswordSection.tsx` 用 state machine + onSubmit (Track E-1)。
 *
 * 状態遷移:
 *   idle → submitting → success | error
 *   error → submitting → ... (再試行可能、idle に戻すには form を unmount)
 *
 * - 入力検証は flow 側 (length / mismatch) と state machine 側 (空欄) の二段
 * - 文言は messageKey で返却し、UI 側で `t()` 適用 (CLAUDE.md §2 ルール 10)
 * - submitting 中は重複送信を防止 (submittingRef)
 */

export type ChangeMasterPasswordState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success' }
  | { kind: 'error'; messageKey: string }

const MESSAGE_KEYS: Record<MasterPasswordError['code'], string> = {
  no_session: 'settings.security.change_master_pw_error.no_session',
  password_too_short: 'settings.security.change_master_pw_error.password_too_short',
  password_mismatch: 'settings.security.change_master_pw_error.password_mismatch',
  wrong_old_password: 'settings.security.change_master_pw_error.wrong_old_password',
  too_many_requests: 'settings.security.change_master_pw_error.too_many_requests',
  network: 'settings.security.change_master_pw_error.network',
  generic: 'settings.security.change_master_pw_error.generic',
}

const messageKeyFor = (err: unknown): string => {
  if (err instanceof MasterPasswordError) return MESSAGE_KEYS[err.code]

  return MESSAGE_KEYS.generic
}

export interface UseChangeMasterPasswordForm {
  state: ChangeMasterPasswordState
  isSubmitting: boolean
  onSubmit: (ev: FormEvent<HTMLFormElement>) => Promise<void>
}

export interface UseChangeMasterPasswordFormOptions {
  /** spec / Settings UI で flow deps を差し替えるための DI */
  deps?: MasterPasswordFlowDeps
}

export const useChangeMasterPasswordForm = (
  options: UseChangeMasterPasswordFormOptions = {},
): UseChangeMasterPasswordForm => {
  const [state, setState] = useState<ChangeMasterPasswordState>({ kind: 'idle' })
  const submittingRef = useRef(false)

  const onSubmit = async (ev: FormEvent<HTMLFormElement>): Promise<void> => {
    ev.preventDefault()
    if (submittingRef.current) return

    const form = ev.currentTarget
    const data = new FormData(form)

    const input: ChangeMasterPasswordInput = {
      oldPassword: String(data.get('oldPassword') ?? ''),
      newPassword: String(data.get('newPassword') ?? ''),
      newPasswordConfirm: String(data.get('newPasswordConfirm') ?? ''),
    }

    submittingRef.current = true
    setState({ kind: 'submitting' })

    try {
      await changeMasterPasswordFlow(input, options.deps)
      setState({ kind: 'success' })
      form.reset()
    } catch (err: unknown) {
      setState({ kind: 'error', messageKey: messageKeyFor(err) })
    } finally {
      submittingRef.current = false
    }
  }

  return {
    state,
    isSubmitting: state.kind === 'submitting',
    onSubmit,
  }
}
